import React from 'react';
import { I18n } from '@aws-amplify/core';
import { ErgonomicToolKeys } from '../../../enums';
import { ErgonomicRiskBadge } from '../ErgonomicRiskBadge';
import {
	KimPushPullReportIcon,
	LibertyMutualReportIcon,
	NioshReportIcon,
	RebaReportIcon,
	StrainIndexReportIcon
} from '@/assets/icons';
import { JDSTooltip } from './JDSTooltip';
import { VibrationTooltip } from './VibrationTooltip';
import type { RelatedReport } from '@/context/Resolution886Report/types';
import * as S from './styles';

const HAND_ARM_VIBRATION_EXPOSURE_NAME = 'hand_arm_vibration_exposure';
const WHOLE_BODY_VIBRATION_EXPOSURE_NAME = 'whole_body_vibration_exposure';

const MOVEMENT = {
	vertical_movement: I18n.get('Vertical movement'),
	lateral_longitudinal_movement: I18n.get('Lateral and longitudinal movement')
};

interface ToolIconLink {
	tool: string;
	icon: JSX.Element;
}

interface ToolsProps {
	relatedReport: RelatedReport | null;
	categoryName: string;
	tool: string | null;
	name: string | null;
}

export function DefaultToolsReview({ tool, name, categoryName, relatedReport }: Readonly<ToolsProps>): JSX.Element {
	const toolIconLink: ToolIconLink[] = [
		{
			tool: ErgonomicToolKeys.NIOSH,
			icon: <NioshReportIcon />
		},
		{
			tool: ErgonomicToolKeys.KIM_PP,
			icon: <KimPushPullReportIcon />
		},
		{
			tool: ErgonomicToolKeys.STRAIN_INDEX,
			icon: <StrainIndexReportIcon />
		},
		{
			tool: ErgonomicToolKeys.REBA,
			icon: <RebaReportIcon />
		},
		{
			tool: ErgonomicToolKeys.LIBERTY_MUTUAL,
			icon: <LibertyMutualReportIcon />
		}
	];

	function getMovement(movement: string): string {
		return MOVEMENT[movement as keyof typeof MOVEMENT];
	}

	function handleTool(tool: string): JSX.Element {
		const toolIcon = toolIconLink.find((item) => item.tool === tool)?.icon || <></>;
		return toolIcon;
	}

	function setNameByTool(name: string): string {
		if (name === HAND_ARM_VIBRATION_EXPOSURE_NAME) {
			return relatedReport && relatedReport.result?.value ? `${relatedReport.result?.value} m/s²` : '-';
		}

		return relatedReport && relatedReport.result?.value
			? `${getMovement(relatedReport.result!.additional_evaluation_option || '')}: ${
					relatedReport.result?.value
				} m/s²`
			: '-';
	}

	function handleCheckVibrationTool(report: RelatedReport | null): boolean {
		const isHandArmVibration = report?.name === HAND_ARM_VIBRATION_EXPOSURE_NAME;
		const isWholeBodyVibration = report?.name === WHOLE_BODY_VIBRATION_EXPOSURE_NAME;
		return !!(report && (isHandArmVibration || isWholeBodyVibration));
	}

	const isHasVibrationTool = handleCheckVibrationTool(relatedReport);
	const isJDS_D86 = tool === ErgonomicToolKeys.JDS_D86;
	const toolIcon = tool ? handleTool(tool) : <></>;
	const vibrationContent = setNameByTool(relatedReport?.name || '');

	return (
		<S.ColDefaultToolsReview span={24}>
			<S.CustomBlock>
				{isJDS_D86 ? (
					<JDSTooltip toolIcon={toolIcon} />
				) : isHasVibrationTool ? (
					<VibrationTooltip toolIcon={toolIcon} categoryName={categoryName} content={vibrationContent} />
				) : (
					<S.CustomContainerTooltip>
						{toolIcon}
						<S.ContentTool isJDS_D86={false}>
							<h4>{I18n.get(categoryName)}</h4>
							<p>{isJDS_D86 ? 'N/A' : name}</p>
						</S.ContentTool>
					</S.CustomContainerTooltip>
				)}
				{relatedReport && <ErgonomicRiskBadge relatedReport={relatedReport} />}
			</S.CustomBlock>
		</S.ColDefaultToolsReview>
	);
}
